import React from 'react';
import {
  ResourceList,
  ResourceItem,
  Stack,
  ButtonGroup,
  Button,
  TextStyle,
  Icon,
  Badge,
  Tooltip,
  Text
} from '@shopify/polaris';
import { DeleteMajor, EditMajor, DuplicateMinor, PlayMajor } from '@shopify/polaris-icons';

/**
 * A component for displaying and managing a list of templates
 * 
 * @param {Object} props
 * @param {Array} props.templates - Array of template objects to display
 * @param {Array} props.selectedTemplates - Array of selected template IDs
 * @param {Function} props.handleTemplateSelect - Callback when a template is selected
 * @param {Function} props.handleDelete - Callback when delete button is clicked
 * @param {Function} props.handleEdit - Callback when edit button is clicked
 * @param {Function} props.handleDuplicate - Callback when duplicate button is clicked
 * @param {Function} props.handleExecute - Callback when execute button is clicked
 * @param {Array} props.aiModels - Array of available AI models
 */
export const TemplateList = ({
  templates = [],
  selectedTemplates = [],
  handleTemplateSelect,
  handleDelete,
  handleEdit,
  handleDuplicate,
  handleExecute,
  aiModels = [] // New prop for available AI models
}) => {
  // Helper function to get model display name
  const getModelDisplayName = (modelId) => {
    if (!modelId || !aiModels || aiModels.length === 0) {
      // Find the default model if no specific model is selected
      const defaultModel = aiModels?.find(m => m.isDefault);
      return defaultModel ? defaultModel.displayName : 'Unknown';
    }
    
    const model = aiModels.find(m => m.id === modelId);
    if (!model) return 'Unknown';
    
    // Add '(Vision)' suffix for vision-capable models
    return model.type === 'vision' ? `${model.displayName} (V)` : model.displayName;
  };

  // Helper function to get model cost per output field
  const getModelCostPerOutput = (modelId) => {
    if (!modelId || !aiModels || aiModels.length === 0) return 1;
    const model = aiModels.find(m => m.id === modelId);
    
    return model ? model.creditCostPerOutput || 1 : 1;
  };

  // Helper function to calculate total credits
  const calculateTotalCredits = (template, modelId) => {
    // For tool templates, always return 0 credits
    if (template.type === 'tool') {
      return 0;
    }
    
    const costPerOutput = getModelCostPerOutput(modelId);
    
    // If the template has a predefined creditCost, use that
    if (template.creditCost) {
      return template.creditCost;
    }
    
    // Handle sidebar-only templates (especially for image templates)
    if (template.sidebarOnly === true) {
      // For sidebar-only templates, count as 1 output × model's cost per output
      return 1 * costPerOutput;
    }
    
    // Count both output fields and output metafields
    const outputFieldsCount = (template.outputFields || []).length;
    const outputMetafieldsCount = (template.outputMetafields || []).length;
    const totalOutputs = outputFieldsCount + outputMetafieldsCount;
    
    // Calculate total credits
    const total = totalOutputs * costPerOutput;
    
    // Always return at least 1 credit if there are no outputs but the template is not marked as sidebarOnly
    return Math.max(total, 1);
  };

  return (
    <ResourceList
      resourceName={{ singular: 'template', plural: 'templates' }}
      items={templates}
      renderItem={(template) => {
        const { id, name, type, modelId, creditCost, outputFields = [], outputMetafields = [] } = template;
        const isSelected = selectedTemplates.includes(id);
        const selectionIndex = selectedTemplates.indexOf(id);
        
        return (
          <ResourceItem
            id={id}
            onClick={() => handleTemplateSelect(id)}
            selected={isSelected}
            accessibilityLabel={`Select ${name}`}
          >
            <Stack alignment="center">
              <Stack.Item>
                {isSelected && (
                  <Badge status="success">{selectionIndex + 1}</Badge>
                )}
              </Stack.Item>
              <Stack.Item fill>
                <TextStyle variation={isSelected ? 'strong' : undefined}>
                  {name || 'Unnamed Template'}
                </TextStyle>
                <div>
                  <Stack spacing="tight">
                    <TextStyle variation="subdued">
                      Type: {
                        type === 'product' ? 'Product' : 
                        type === 'variant' ? 'Variant' : 
                        type === 'image' ? 'Image' : 
                        type === 'tool' ? 'Tool Execution' : 
                        'Unknown'
                      }
                    </TextStyle>
                    {type !== 'tool' && (
                      <>
                        <TextStyle variation="subdued">
                          •
                        </TextStyle>
                        <TextStyle variation="subdued">
                          Model: {getModelDisplayName(modelId)}
                        </TextStyle>
                      </>
                    )}
                    <TextStyle variation="subdued">
                      •
                    </TextStyle>
                    {type === 'tool' ? (
                      <Badge status="success">No AI Credits</Badge>
                    ) : (
                      <Tooltip content={`${outputFields.length + outputMetafields.length} output fields × ${getModelCostPerOutput(modelId)} credits per field`}>
                        <Badge status="info">Credits: {calculateTotalCredits(template, modelId)}</Badge>
                      </Tooltip>
                    )}
                  </Stack>
                </div>
              </Stack.Item>
              {(handleExecute || handleEdit || handleDuplicate || handleDelete) && (
                <Stack.Item>
                  <ButtonGroup segmented>
                    {handleExecute && (
                      <Button 
                        icon={<Icon source={PlayMajor} color="success" />} 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExecute(id);
                        }}
                        accessibilityLabel={`Execute ${name}`}
                      />
                    )}
                    {handleEdit && (
                      <Button 
                        icon={<Icon source={EditMajor} color="base" />} 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(template);
                        }}
                        accessibilityLabel={`Edit ${name}`}
                      />
                    )}
                    {handleDuplicate && (
                      <Button 
                        icon={<Icon source={DuplicateMinor} color="base" />} 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(template);
                        }}
                        accessibilityLabel={`Duplicate ${name}`}
                      />
                    )}
                    {handleDelete && (
                      <Button 
                        icon={<Icon source={DeleteMajor} color="critical" />} 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(template.id);
                        }}
                        accessibilityLabel={`Delete ${name}`}
                        destructive
                      />
                    )}
                  </ButtonGroup>
                </Stack.Item>
              )}
            </Stack>
          </ResourceItem>
        );
      }}
    />
  );
};

export default TemplateList;
